'use client';

// ═══ ФОРМА НАБОРА (вид 2026, решение владельца 2026-08-10) ═══
//
// Прежний вид форм владелец забраковал целиком («хуёвый внешний вид»), поэтому здесь
// НЕ косметика старой разметки, а другой вид, собранный по действующим правилам:
//  1. подпись поля видна ВСЕГДА (подсказка-внутри исчезает при вводе — так нельзя);
//  2. теней в содержимом нет: слои различаются оттенком (фон → группа → поле), как в
//     iOS 26 и Material 3 — отсюда «чисто», а не «мыльно»;
//  3. проверка на лету: заполнено верно — галочка справа в поле, не заполнено —
//     красная строка сразу, а не после нажатия «Сохранить»;
//  4. главное действие — на плавающей стеклянной панели у низа экрана: всегда под
//     пальцем, мотать вниз не надо;
//  5. выбор из 2–4 вариантов — переключателем прямо в поле, без слоёв и выпадашек
//     (владелец 2026-08-10: «каждое нажатие открывать шторку» — запрещено).
//
// Вид описан в `kit.css`, раздел «Форма». Своих цветов и размеров страницы не заводят.
import { useEffect, type ReactNode } from 'react';
import { cx } from './cx';

/** Группа полей: одна смысловая пачка на своей поверхности. */
export function KFormGroup({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx('k kf-grp', className)}>{children}</div>;
}

/**
 * Заголовок раздела над группой: КОРОТКОЕ имя (2–3 слова) и, если надо, пояснение
 * помельче отдельной строкой. Пояснение в заголовок не сливать: длинный жирный абзац
 * читается как стена, а не как имя раздела.
 */
export function KFormCaption({ title, hint }: { title: ReactNode; hint?: ReactNode }) {
  return (
    <div className="k kf-cap">
      <b>{title}</b>
      {hint ? <span>{hint}</span> : null}
    </div>
  );
}

/**
 * Поле формы: подпись сверху (видна всегда), контрол, под ним пояснение или ошибка.
 * `required` — СЛОВО «обязательно» рядом с подписью (звёздочку человек не читает);
 * слово даёт страница, потому что кабинет двуязычный, а набор переводов не знает.
 */
export function KField({
  label,
  required,
  hint,
  error,
  children,
}: {
  label?: ReactNode;
  required?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  // Появилась ошибка — короткая вибрация на телефоне (iOS: haptic warning у поля,
  // владелец 26.09: «все ошибки с вибрацией»). В iOS-приложении (класс `app` на <html>)
  // вибрацию даёт мост — второй раз не дёргаем; на десктопе (мышь) — не зовём вовсе.
  const hasError = Boolean(error);
  useEffect(() => {
    if (!hasError || typeof window === 'undefined') return;
    if (document.documentElement.classList.contains('app')) return;
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    navigator.vibrate?.(30);
  }, [hasError]);
  return (
    <div className="kf-fld">
      {label ? (
        <label className="kf-lbl">
          {label}
          {required ? <i>{required}</i> : null}
        </label>
      ) : null}
      {children}
      {error ? <p className="kf-bad">{error}</p> : hint ? <p className="kf-note">{hint}</p> : null}
    </div>
  );
}

/**
 * Выбор из 2–4 вариантов кнопками в самом поле. Больше вариантов — это уже список
 * (`SmartSelect`/`KSelect`), сюда их не втискивать: подписи слипнутся.
 *
 * `size='sm'` — тот же выбор ПИЛЮЛЯМИ по ширине подписей (не на всю строку): для мест,
 * где сегмент стоит не в поле формы, а внутри карточки — например режим группы условий
 * на карточке конструктора (владелец 16.08.2026: у переключателя видна была только
 * текущая подпись и читалась как название настройки, «давай две пилюли сделаем»).
 *
 * `tone` — выделение НЕ фирменным розовым, а тоном места: сегмент берёт цвет из
 * переменных обёртки (`--seg-tone` — база тона, `--seg-surf` — подложка, на которой он
 * лежит) и красит выбранное слово по общему рецепту громкости. Заведено для тумблера
 * «и | или» на карточке условия (С29, владелец 24.08.2026: «розовый брендовый акцент из
 * тумблера убрать, выбранное — пилюля тоном СВОЕЙ группы»). Разметку сегмента ради этого
 * копировать по месту нельзя — поэтому тон приходит пропом, а не вторым контролом.
 *
 * `off` у пункта — ПОГАШЕННЫЙ пункт (ТЗ WEB-LOOK Э9 строка 2, вкладки шторки скрепки): он
 * серый (`aria-disabled`), но нажимается — `onChange` приходит и для него, а зовущий решает,
 * что показать вместо переключения (причину плашкой). Как погашенная вкладка таб-бара iOS.
 */
export function KSegment({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
  tone,
  className,
}: {
  value: string;
  options: readonly { value: string; label: string; off?: boolean }[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  size?: 'md' | 'sm';
  tone?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx('k kf-seg', size === 'sm' && 'kf-seg--sm', tone && 'kf-seg--tone', className)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          {...(o.off ? { 'aria-disabled': true } : {})}
          className={cx('kf-seg__i', o.value === value && 'is-on', o.off && 'is-off')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Выбор из нескольких вариантов СТРОКАМИ прямо в карточке: название, под ним пояснение,
 * у выбранного — галочка справа. Ничего не всплывает и не накрывает содержимое.
 *
 * Заказ владельца 2026-08-10 по образцу приложения Claude (скрин «Tool access»): у нас
 * длинные варианты жили в выпадашке, которая ложилась поверх формы — не видно ни к чему
 * она относится, ни соседних вариантов. Правило: варианты с ПОЯСНЕНИЕМ или длинной
 * подписью — сюда; короткий разовый выбор на странице — по-прежнему `PopSelect`.
 */
export function KChoice({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  /** `accent` — выделенный пункт («Своё» первым, QUICK-START §11.7 п.2): мягкая подложка. */
  options: readonly { value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean; accent?: boolean }[];
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="k kf-ch" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          className={cx('kf-ch__row', o.value === value && 'is-on', o.accent && 'is-accent')}
          onClick={() => onChange(o.value)}
        >
          <span className="kf-ch__t">
            <b>{o.label}</b>
            {o.hint ? <s>{o.hint}</s> : null}
          </span>
          {/* Галочка стоит В КАЖДОЙ строке, у невыбранных — невидимой: место под неё
              держится всегда, поэтому выбор не перекраивает текст соседей. */}
          <svg
            className={cx('kf-ch__tick', o.value !== value && 'is-off')}
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4.5 12.5l5 5 10-11" />
          </svg>
        </button>
      ))}
    </div>
  );
}

/**
 * Те же строки с галочкой, но отмечаются НЕСКОЛЬКО (владелец 11.08.2026: «раз строка
 * отмечается галочкой — пусть отмечаются обе», шаг «Что запускает» мастера). Вид и
 * числа — один в один KChoice; отличие только в роли (checkbox) и значении-массиве.
 * Ряды-тумблеры для такого не годятся: тумблер — про вкл/выкл настройки, а здесь
 * выбор вариантов, как на соседних шагах.
 */
export function KChoiceMany({
  values,
  options,
  onChange,
  ariaLabel,
}: {
  values: readonly string[];
  options: readonly { value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean }[];
  onChange: (values: string[]) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="k kf-ch" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            role="checkbox"
            aria-checked={on}
            disabled={o.disabled}
            className={cx('kf-ch__row', on && 'is-on')}
            onClick={() =>
              onChange(on ? values.filter((v) => v !== o.value) : [...values, o.value])
            }
          >
            <span className="kf-ch__t">
              <b>{o.label}</b>
              {o.hint ? <s>{o.hint}</s> : null}
            </span>
            <svg
              className={cx('kf-ch__tick', !on && 'is-off')}
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.3}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M4.5 12.5l5 5 10-11" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Плавающая панель действий у низа. Липнет к низу прокрутки, фон размывается —
 * содержимое проезжает под ней и видно, что список продолжается.
 *
 * INVARIANT(scriptera): панель НЕ position:fixed. Форма живёт внутри шторки, у которой
 * свой скролл и свой безопасный низ; fixed вырывает панель из шторки и на телефоне
 * встаёт поверх нативной клавиатуры. Причина: docs/NATIVE-SLIDE-PLAN.md §11.
 */
export function KActionBar({ children }: { children: ReactNode }) {
  return <div className="k kf-bar">{children}</div>;
}
